// Hộp đánh giá khi đăng xuất (docs/KE_HOACH_DANH_GIA_NEN_TANG.md) — đặt một lần trong AppShell, mở khi askLogoutSurvey
// nhận được bộ câu hỏi. Điện thoại: tấm trượt từ đáy; máy tính: hộp giữa màn. Gửi (đủ câu bắt buộc) hoặc Bỏ qua đều đăng
// xuất; X / Esc / chạm ra ngoài = ở lại, không ghi gì. Câu hỏi: chấm sao 1–5, thang điểm 0–10, chọn một, chọn nhiều, chữ.

import { useId, useState } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Check, Star, X } from 'lucide-react'
import {
  SCALE_MAX,
  SCALE_MIN,
  STARS_MAX,
  SURVEY_TEXT_MAX,
  isAnswered,
  type SurveyAnswer,
  type SurveyAnswers,
  type SurveyQuestion,
} from '@shoptrack/common/platform-survey'
import { useIsMobile } from '@/hooks/useIsMobile'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { cancelLogoutSurvey, finishLogoutSurvey, useLogoutSurvey, type LogoutSurvey } from './logout-survey'

export function LogoutSurveyHost() {
  const p = useLogoutSurvey()
  return p ? <LogoutSurveyDialog key={p.survey.id} survey={p.survey} /> : null
}

const STAR_LABEL = ['Rất tệ', 'Tệ', 'Bình thường', 'Tốt', 'Rất tốt']

function StarsInput({ value, onChange, labelId }: { value: number | undefined; onChange: (v: number) => void; labelId: string }) {
  const [hover, setHover] = useState<number | null>(null)
  const shown = hover ?? value ?? 0
  return (
    <div>
      <div role="radiogroup" aria-labelledby={labelId} className="flex items-center gap-1" onPointerLeave={() => setHover(null)}>
        {Array.from({ length: STARS_MAX }, (_, i) => i + 1).map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} sao`}
            onClick={() => onChange(n)}
            onPointerEnter={(e) => e.pointerType === 'mouse' && setHover(n)}
            className="motion-state grid size-11 place-items-center rounded-xl hover:bg-paperSoft active:scale-95"
          >
            <Star className={cn('size-8 transition-colors', n <= shown ? 'fill-warn text-warn' : 'text-rule')} strokeWidth={1.5} aria-hidden />
          </button>
        ))}
      </div>
      <p className="mt-1 h-4 text-xs font-medium text-inkSoft" aria-live="polite">
        {shown ? STAR_LABEL[shown - 1] : ''}
      </p>
    </div>
  )
}

function ScaleInput({ value, onChange, labelId }: { value: number | undefined; onChange: (v: number) => void; labelId: string }) {
  const phone = useIsMobile()
  return (
    <div>
      <div role="radiogroup" aria-labelledby={labelId} className={cn('grid gap-1.5', phone ? 'grid-cols-6' : 'grid-cols-11')}>
        {Array.from({ length: SCALE_MAX - SCALE_MIN + 1 }, (_, i) => SCALE_MIN + i).map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            onClick={() => onChange(n)}
            className={cn(
              'motion-state h-11 rounded-lg border text-sm font-semibold tabular-nums desktop:h-10',
              value === n ? 'border-accent bg-accent text-onAccent' : 'border-rule bg-raise text-ink hover:bg-paperSoft',
            )}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-xs text-muted">
        <span>{SCALE_MIN} · Không</span>
        <span>Chắc chắn · {SCALE_MAX}</span>
      </div>
    </div>
  )
}

function ChoiceInput({ q, value, onChange, labelId }: { q: SurveyQuestion; value: SurveyAnswer | undefined; onChange: (v: SurveyAnswer) => void; labelId: string }) {
  const multi = q.type === 'multi'
  const picked = multi ? (Array.isArray(value) ? value : []) : typeof value === 'string' ? [value] : []
  const toggle = (o: string) => {
    if (!multi) return onChange(o)
    onChange(picked.includes(o) ? picked.filter((x) => x !== o) : [...picked, o])
  }
  return (
    <div role={multi ? 'group' : 'radiogroup'} aria-labelledby={labelId} className="flex flex-wrap gap-2">
      {(q.options ?? []).map((o) => {
        const on = picked.includes(o)
        return (
          <button
            key={o}
            type="button"
            role={multi ? 'checkbox' : 'radio'}
            aria-checked={on}
            onClick={() => toggle(o)}
            className={cn(
              'motion-state inline-flex min-h-11 items-center gap-2 rounded-full border px-4 text-sm font-medium desktop:min-h-10',
              on ? 'border-accent bg-accent/10 text-accent' : 'border-rule bg-raise text-ink hover:bg-paperSoft',
            )}
          >
            {multi && (
              <span className={cn('grid size-4 place-items-center rounded border', on ? 'border-accent bg-accent text-onAccent' : 'border-muted/50')} aria-hidden>
                {on && <Check className="size-3" strokeWidth={3} />}
              </span>
            )}
            {o}
          </button>
        )
      })}
    </div>
  )
}

function QuestionField({ q, value, onChange }: { q: SurveyQuestion; value: SurveyAnswer | undefined; onChange: (v: SurveyAnswer) => void }) {
  const labelId = useId()
  return (
    <fieldset className="min-w-0">
      <legend id={labelId} className="mb-3 text-[15px] font-semibold text-ink">
        {q.label}
        {q.required && <span className="ml-0.5 text-danger" aria-hidden>*</span>}
      </legend>
      {q.type === 'stars' ? (
        <StarsInput value={typeof value === 'number' ? value : undefined} onChange={onChange} labelId={labelId} />
      ) : q.type === 'scale' ? (
        <ScaleInput value={typeof value === 'number' ? value : undefined} onChange={onChange} labelId={labelId} />
      ) : q.type === 'text' ? (
        <Textarea
          aria-labelledby={labelId}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          maxLength={SURVEY_TEXT_MAX}
          rows={3}
          className="min-h-[84px]"
        />
      ) : (
        <ChoiceInput q={q} value={value} onChange={onChange} labelId={labelId} />
      )}
    </fieldset>
  )
}

function LogoutSurveyDialog({ survey }: { survey: LogoutSurvey }) {
  const phone = useIsMobile()
  const [answers, setAnswers] = useState<SurveyAnswers>({})
  const [busy, setBusy] = useState<'send' | 'skip' | null>(null)
  const ready = survey.questions.every((q) => !q.required || isAnswered(q, answers[q.id]))
  const finish = async (kind: 'send' | 'skip') => {
    if (busy) return
    setBusy(kind)
    await finishLogoutSurvey(kind === 'send' ? answers : null)
  }

  return (
    <DialogPrimitive.Root open onOpenChange={(open) => !open && !busy && cancelLogoutSurvey()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            phone ? 'gx-scrim' : 'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          data-survey=""
          // Mở hộp: focus vào chính hộp (không vào nút X — tránh viền focus đậm ngay khi vừa hiện)
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            ;(e.currentTarget as HTMLElement | null)?.focus()
          }}
          tabIndex={-1}
          className={cn(
            'fixed z-50 flex flex-col bg-raise shadow-overlay outline-none focus-visible:ring-0 focus-visible:ring-offset-0 data-[state=open]:animate-in',
            phone
              ? 'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-3xl pb-[max(1rem,env(safe-area-inset-bottom))] data-[state=open]:slide-in-from-bottom'
              : 'left-1/2 top-1/2 max-h-[88dvh] w-[min(92vw,480px)] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-rule data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          )}
        >
          {phone && <div className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-rule" aria-hidden />}
          <div className={cn('flex shrink-0 items-center justify-between gap-3', phone ? 'px-5 pt-2' : 'px-6 pt-4')}>
            <DialogPrimitive.Title className="text-lg font-semibold text-ink">{survey.title}</DialogPrimitive.Title>
            <DialogPrimitive.Close
              disabled={!!busy}
              className="-mr-2 grid size-11 place-items-center rounded-full text-inkSoft hover:bg-paperSoft active:bg-paperSoft disabled:opacity-40"
              aria-label="Đóng, không đăng xuất"
            >
              <X className="size-5" aria-hidden />
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="sr-only">Đánh giá trước khi đăng xuất</DialogPrimitive.Description>

          <div className={cn('min-h-0 flex-1 space-y-6 overflow-y-auto pb-2 pt-4', phone ? 'px-5' : 'px-6')}>
            {survey.questions.map((q) => (
              <QuestionField key={q.id} q={q} value={answers[q.id]} onChange={(v) => setAnswers((a) => ({ ...a, [q.id]: v }))} />
            ))}
          </div>

          <div className={cn('grid shrink-0 grid-cols-2 gap-2 border-t border-rule pt-3', phone ? 'px-5' : 'px-6 pb-5')}>
            <Button type="button" variant="outline" disabled={!!busy} onClick={() => finish('skip')}>
              {busy === 'skip' ? 'Đang thoát…' : 'Bỏ qua'}
            </Button>
            <Button type="button" disabled={!ready || !!busy} onClick={() => finish('send')}>
              {busy === 'send' ? 'Đang gửi…' : 'Gửi'}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
